import { ReactCta } from '@/components/react/ui/ReactCta';
import { ReactHeading } from '@/components/react/ui/ReactHeading';
import { ReactIcon } from '@/components/react/ui/ReactIcon';
import { Dialog } from '@base-ui-components/react/dialog';
import type { ReactNode } from 'react';
import React from 'react';
import invariant from 'tiny-invariant';

export interface ModalStep {
  title: string;
  description: string;
  content: ReactNode;
  tip?: {
    title: string;
    description: string;
  };
  nextText: string;
}

interface SteppedModalProps {
  title: string;
  modalBackText: string;
  modalCloseText: string;
  modalTriggerText: string;
  steps: ModalStep[];
}

const StepModalTip: React.FC<{ tip: ModalStep['tip'] }> = ({ tip }) => {
  if (!tip) return null;

  return (
    <div className="bg-blurb-bg-brand-subtlest flex items-start gap-2 p-4">
      <span className="py-0.5">
        <ReactIcon name="info" size="large" className="text-icon-brand" />
      </span>
      <div className="flex flex-col justify-start gap-1">
        <ReactHeading size="h4">{tip.title}</ReactHeading>
        <span className="text-blurb-text-subtle font-sans text-sm">
          {tip.description}
        </span>
      </div>
    </div>
  );
};

const ModalStep: React.FC<ModalStep & { stepNum: number }> = ({
  title,
  description,
  content,
  tip,
  stepNum,
}) => {
  return (
    <React.Fragment>
      <div className="flex flex-col gap-6 md:gap-8">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <div className="bg-blurb-bg-brand text-blurb-text-inverse flex min-h-[24px] min-w-[24px] items-center justify-center rounded-full text-sm md:min-h-[30px] md:min-w-[30px]">
              {stepNum}
            </div>
            <ReactHeading size="h4">{title}</ReactHeading>
          </div>
          <span>{description}</span>
        </div>
        {content}
        <StepModalTip tip={tip} />
      </div>
    </React.Fragment>
  );
};

export const SteppedModal: React.FC<SteppedModalProps> = ({
  title,
  modalBackText,
  modalTriggerText,
  modalCloseText,
  steps,
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [currentStep, step] = React.useReducer(
    (state, payload: { type: 'inc' | 'dec' | 'set'; value?: number }) => {
      switch (payload.type) {
        case 'inc': {
          return state + 1 > steps.length - 1 ? state : state + 1;
        }
        case 'dec': {
          return state - 1 < 0 ? state : state - 1;
        }
        case 'set': {
          return payload.value || 0;
        }
        default: {
          return state;
        }
      }
    },
    0,
  );

  invariant(steps[currentStep], 'Invalid step defined');

  const onBack = React.useCallback(() => {
    step({ type: 'dec' });

    scrollRef.current?.scrollTo({ top: 0 });
  }, [step]);

  const onForward = React.useCallback(() => {
    step({ type: 'inc' });

    scrollRef.current?.scrollTo({ top: 0 });
  }, [step]);

  const backButton = React.useMemo(
    () => (
      <ReactCta intent="promo" variant="subtle" fullWidth onClick={onBack}>
        {modalBackText}
      </ReactCta>
    ),
    [modalBackText, onBack],
  );

  const nextButton = React.useMemo(
    () => (
      <ReactCta intent="promo" variant="filled" fullWidth onClick={onForward}>
        {steps[currentStep]!.nextText}
      </ReactCta>
    ),
    [steps, currentStep, onForward],
  );

  const onOpenChange = React.useCallback((open: boolean) => {
    if (!open) {
      step({ type: 'set', value: 0 });
    }
  }, []);

  return (
    <Dialog.Root onOpenChangeComplete={onOpenChange}>
      <Dialog.Trigger className="text-blurb-text-link hover:text-blurb-text-link-hover active:text-blurb-text-link-hover flex cursor-pointer flex-nowrap items-center gap-1 font-medium">
        <ReactIcon name="info" />
        <span className="underline">{modalTriggerText}</span>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 bg-black opacity-50 transition-all duration-150 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <Dialog.Popup className="fixed inset-0 m-auto flex h-min max-h-[98%] w-[300px] flex-col gap-6 rounded-lg bg-white transition-all duration-150 data-[ending-style]:scale-90 data-[ending-style]:opacity-0 data-[starting-style]:scale-90 data-[starting-style]:opacity-0 md:w-[580px] md:gap-10">
          <div className="flex flex-col-reverse items-start justify-between gap-6 self-stretch px-4 pt-4 md:flex-row md:items-center md:gap-0 md:px-10 md:pt-8">
            <Dialog.Title render={<h4 />}>{title}</Dialog.Title>
            <Dialog.Close className="text-blurb-text-link flex cursor-pointer items-center gap-1 self-end md:self-auto">
              <span className="text-sm/relaxed">{modalCloseText}</span>
              <ReactIcon name="close" size="small" className="mt-0.5" />
            </Dialog.Close>
          </div>
          <Dialog.Description
            className="flex flex-col gap-6 overflow-auto px-4 pb-4 md:gap-8 md:px-10 md:pb-8"
            render={<div ref={scrollRef} />}
          >
            <ModalStep stepNum={currentStep + 1} {...steps[currentStep]} />
            <div className="flex flex-col justify-evenly gap-2 md:flex-row md:gap-4">
              {currentStep === 0 ?
                <Dialog.Close render={backButton} />
              : backButton}
              {currentStep === steps.length - 1 ?
                <Dialog.Close render={nextButton} />
              : nextButton}
            </div>
          </Dialog.Description>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
